/**
 * CRM Module - Role Constants
 *
 * Определяет роли модуля CRM.
 * Используется для типизации, проверок прав и сидов.
 */

export const CRM_ROLES = {
  CRM_SALESPERSON: 'CRM_SALESPERSON',
  CRM_OWNER: 'CRM_OWNER',
  CRM_INTERN: 'CRM_INTERN',
  CRM_BRANCH_MANAGER: 'CRM_BRANCH_MANAGER',
  /**
   * Старший менеджер лидов. Полный доступ к CRM + право архивировать лиды
   * (середина между обычным салеспёрсоном без права архива и руководителем
   * филиала).
   */
  CRM_LEAD_MANAGER: 'CRM_LEAD_MANAGER',
} as const;

/**
 * Определения ролей для сидов и UI
 */
export const CRM_ROLE_DEFS = [
  {
    role_code: CRM_ROLES.CRM_SALESPERSON,
    name: 'crm_salesperson',
    description: 'CRM Salesperson - Full access to CRM module',
    is_active: true,
  },
  {
    role_code: CRM_ROLES.CRM_OWNER,
    name: 'crm_owner',
    description: 'CRM Owner - Full access to CRM module',
    is_active: true,
  },
  {
    role_code: CRM_ROLES.CRM_INTERN,
    name: 'crm_intern',
    description: 'CRM Intern - Read-only access to CRM module (no update/delete)',
    is_active: true,
  },
  {
    role_code: CRM_ROLES.CRM_BRANCH_MANAGER,
    name: 'crm_branch_manager',
    description: 'CRM Branch Manager - Full CRM access + partner delete/archive',
    is_active: true,
  },
  {
    role_code: CRM_ROLES.CRM_LEAD_MANAGER,
    name: 'crm_lead_manager',
    description: 'CRM Lead Manager - Full CRM access + lead archive',
    is_active: true,
  },
] as const;

/**
 * TypeScript тип для ролей CRM
 */
export type CrmRole = (typeof CRM_ROLES)[keyof typeof CRM_ROLES];

/**
 * Действия над контактными данными партнёра — контактными лицами и каналами
 * связи (телефоны, почты) как самого партнёра, так и его контактных лиц.
 *
 * Добавление и правка разведены намеренно: у клиента может быть политика
 * «заводить новое можно всегда, а переписывать заведённое — нет». Одним кодом
 * такую политику не выразить.
 *
 * Только контракт, без политики: доменные методы модуля спрашивают разрешение
 * через `PartnerRepository.can(<код>, params)`, а КТО и КОГДА имеет право —
 * решает инстанс своим guard'ом (`onCan`). Инстанс guard не повесил → действие
 * разрешено всем, у кого есть доступ к методу (дефолт `can()` при отсутствии
 * guards). Тот же паттерн, что PROCUREMENT_ACTION_EDIT_PRICE.
 *
 * Нужны потому, что связи контактов живут в lnk-таблицах и пишутся напрямую,
 * мимо CRUD партнёра: `canUpdate` партнёра на них не срабатывает.
 *
 * Живут в common: строки делят серверная проверка и браузерный бандл.
 */

/**
 * «Завести новое»: добавить канал связи, привязать канал или контактное лицо к
 * партнёру.
 *
 * params: `{ partnerId }`
 */
export const PARTNER_ACTION_ADD_CONTACTS = 'addPartnerContacts';

/**
 * «Изменить заведённое»: правка и удаление канала, правка карточки контактного
 * лица, отвязка от партнёра, смена признаков «основной»/«активен».
 *
 * params: `{ partnerId, channelId?, contactPersonId?, recordCreatedAt? }` —
 * помимо партнёра передаём саму затронутую запись, чтобы политика могла
 * считать окно правки от момента её появления, а не от даты партнёра.
 */
export const PARTNER_ACTION_EDIT_CONTACTS = 'editPartnerContacts';

/**
 * «Вести примечание партнёра» — отдельное действие у `PartnerRepository.setNotes`.
 *
 * Примечание намеренно вынесено из общего `update()`: платформа зовёт
 * `canUpdate(id)` без имени поля, поэтому одним ответом нельзя одновременно
 * закрыть реквизиты и оставить открытым примечание.
 *
 * params: `{ partnerId }`
 */
export const PARTNER_ACTION_EDIT_NOTES = 'editPartnerNotes';

/**
 * «Завести партнёра с ролями и каналами» — точка политики ровно у
 * `PartnerRepository.createExtended`.
 *
 * Имя намеренно называет метод, а не создание вообще: проверка висит только на
 * этом пути. Голый `create()` — в том числе программные импорты (обмен 1С,
 * загрузчики) — её не проходит, и требовать там заполненных полей нельзя.
 *
 * Вынесено отдельным действием, потому что роли партнёра приходят в
 * `createExtended` полем `role_ids` и вырезаются из payload до `create()`:
 * `onCanCreate` видит только колонки и про будущие роли не знает. Через params
 * политика получает и роли, и остальные поля — например правила Grasias
 * «клиента нельзя завести без источника и сферы деятельности».
 *
 * Без guard'ов действие разрешено, как и было: другие инстансы ничего не теряют.
 *
 * params: `{ roleIds, sourceId, segmentIds }` — роли и сегменты (сферы
 * деятельности), которые будут назначены в этом же вызове, и выбранный
 * источник партнёра.
 */
export const PARTNER_ACTION_CREATE_EXTENDED = 'createPartnerExtended';
